import { useCallback, useRef } from 'react';

import { useEvent } from '~features/events/hooks/useEvent';

import {
  CONNECTION_SURFACE_EVENTS,
  type SearchPerformanceAnalyticsEventProperties,
} from './SearchPerformanceAnalyticsEvents';
import useSearchPerformanceCommonEventProperties from './useSearchPerformanceCommonEventProperties';

type TrackSearchPerformanceEvent = <E extends keyof SearchPerformanceAnalyticsEventProperties>(
  ...args: SearchPerformanceAnalyticsEventProperties[E] extends void
    ? [event: E]
    : [event: E, properties: SearchPerformanceAnalyticsEventProperties[E]]
) => void;

function useTrackSearchPerformanceEvent(): TrackSearchPerformanceEvent {
  const { track } = useEvent();
  // The three properties PRE-3271 attaches to every event: `company_id` is
  // added server-side, so the tracker merges in the other two on every call.
  // Per-event properties win — `Domain Selector Changed` overrides `domain`
  // with the post-switch value the pre-switch selection here cannot give.
  const commonProperties = useSearchPerformanceCommonEventProperties();
  // useEvent().track and the common properties are unstable (new ref every
  // render); refs keep the callback identity stable so consumers can safely
  // list it as an effect dependency while it still reads the latest values.
  const trackRef = useRef(track);
  trackRef.current = track;
  const commonRef = useRef(commonProperties);
  commonRef.current = commonProperties;

  return useCallback<TrackSearchPerformanceEvent>(
    ((event: keyof SearchPerformanceAnalyticsEventProperties, properties?: Record<string, unknown>) => {
      const common = CONNECTION_SURFACE_EVENTS.has(event) ? { ...commonRef.current, domain: '' } : commonRef.current;
      trackRef.current(event, { ...common, ...properties });
    }) as TrackSearchPerformanceEvent,
    []
  );
}

export default useTrackSearchPerformanceEvent;
